import { isSourceType, Platform } from '@foundation-player/runtime';
import { isArrayOf } from '@foundation-player/utils';
import { PlayerType } from '../../builder-config';
import { TransformPluginConfigs } from './transform-plugin-configs';

/**
 * This function is used to evaluate the plugin configuration for the SSAI (server-side-ad-insertion) Plugin.
 *
 * @remarks
 * The plugin is enabled by default for all clients unless explicitly disabled.
 *
 * If the client only wants to play progressive streams, the plugin can be disabled, because it is not needed.
 * This is checked via the playerType, if the playerType is Native, the plugin is disabled.
 *
 * If SSAI is enabled, the Yospace SDK can be enabled as well. Be aware that our SDK integration is still experimental,
 * thus the SDK has to be whitelisted for specific source types as well.
 * Also note that the SDK is only supported for Bitmovin player at the moment.
 */
export const addSsaiPlugin: TransformPluginConfigs = (
  partialBuilderConfig,
  pluginConfigs,
  _clientAndScope,
  _environment,
  platform,
) => {
  const ssai = 'foundation.plugin.ssai';
  const { remoteConfig, playerType } = partialBuilderConfig;
  const userSsaiPluginConfig = pluginConfigs[ssai];

  if (userSsaiPluginConfig?.enabled === false) {
    return pluginConfigs;
  }
  if (!userSsaiPluginConfig?.enabled && remoteConfig.ssai?.enable === false) {
    return pluginConfigs;
  }
  if (playerType === PlayerType.Native) {
    return pluginConfigs;
  }

  // note: use remote config as a fallback only
  // note: arrays won't be merged
  const yospaceSdkShouldBeEnabledForSourceTypes = isArrayOf(
    userSsaiPluginConfig?.yospaceSdkEnabledForSourceTypes,
    isSourceType,
  )
    ? userSsaiPluginConfig.yospaceSdkEnabledForSourceTypes
    : isArrayOf(remoteConfig.ssai?.enableYospaceSdkForSourceTypes, isSourceType)
      ? remoteConfig.ssai.enableYospaceSdkForSourceTypes
      : [];

  const isRootPlayerTypeSupportedByYospaceSdkAdapter =
    playerType === PlayerType.Bitmovin || playerType === PlayerType.DashJs;
  const yospaceSdkEnabledForSourceTypes = isRootPlayerTypeSupportedByYospaceSdkAdapter
    ? yospaceSdkShouldBeEnabledForSourceTypes
    : [];

  return {
    ...pluginConfigs,
    [ssai]: {
      ...userSsaiPluginConfig,
      enabled: true,
      fetchAtvId: userSsaiPluginConfig?.fetchAtvId ?? platform === Platform.Web,
      atvTimeout: userSsaiPluginConfig?.atvTimeout || remoteConfig.advertisement?.timeouts?.atv,
      ang_ref: remoteConfig.ssai?.angRef,
      ang_bundle: remoteConfig.ssai?.angBundle,
      yospaceSdkEnabledForSourceTypes,
    },
  };
};
